import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, Clock, Trophy, Save, Globe, Lock, Share2, Trash2, UserPlus, X, Crown, Users, ShieldCheck } from 'lucide-react';
import { deleteCompetitionApi } from '../services/api';
import { supabase } from '../services/supabaseClient';
import Swal from 'sweetalert2';
import { translateError } from '../utils/errorTranslator';

function useMobile() {
    const [mobile, setMobile] = useState(window.innerWidth < 700);
    useEffect(() => {
        const h = () => setMobile(window.innerWidth < 700);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    return mobile;
}

export default function CompetitionDetails({ competition, onUpdate, onUpdateCoManagers, onUpdateCoOwners, onDelete, isOwner = false, isCoOwner = false, isCoManager = false, readOnly = false, currentUserEmail = '' }) {
    const isMobile = useMobile();
    const [formData, setFormData] = useState({
        name: competition?.name || '',
        address: competition?.address || '',
        event_date: competition?.event_date || '',
        start_time: competition?.start_time || '',
        weigh_in_date: competition?.weigh_in_date || '',
        weigh_in_time: competition?.weigh_in_time || '',
        visibility: competition?.visibility || 'private'
    });
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberRole, setNewMemberRole] = useState('co_manager');
    const [ownerEmail, setOwnerEmail] = useState('');

    useEffect(() => {
        if (competition?.owner_id && !isOwner) {
            supabase.rpc('get_owner_email', { owner_id: competition.owner_id })
                .then(({ data }) => { if (data) setOwnerEmail(data); });
        }
    }, [competition?.owner_id, isOwner]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const coManagers = competition?.co_managers || [];
    const coOwners = competition?.co_owners || [];
    const allMembers = [
        ...coOwners.map(email => ({ email, role: 'co_owner' })),
        ...coManagers.map(email => ({ email, role: 'co_manager' }))
    ];

    const handleAddMember = async () => {
        const email = newMemberEmail.trim().toLowerCase();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            Swal.fire({ icon: 'warning', title: 'Email invalide', text: 'Veuillez saisir une adresse email valide.' });
            return;
        }
        if (coManagers.includes(email) || coOwners.includes(email)) {
            Swal.fire({ icon: 'info', title: 'Déjà ajouté', text: 'Cette adresse a déjà accès à la compétition.' });
            return;
        }
        if (newMemberRole === 'co_owner') {
            await onUpdateCoOwners([...coOwners, email]);
        } else {
            await onUpdateCoManagers([...coManagers, email]);
        }
        setNewMemberEmail('');
        const roleLabel = newMemberRole === 'co_owner' ? 'co-propriétaire' : 'co-organisateur';
        Swal.fire({
            icon: 'success',
            title: 'Accès accordé',
            text: `${email} a été ajouté comme ${roleLabel}.`,
            toast: true, position: 'top-end', timer: 4000, showConfirmButton: false, timerProgressBar: true
        });
    };

    const handleRemoveMember = async (email, role) => {
        const result = await Swal.fire({
            title: 'Retirer cet accès ?',
            text: email,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e53e3e',
            confirmButtonText: 'Retirer',
            cancelButtonText: 'Annuler'
        });
        if (result.isConfirmed) {
            if (role === 'co_owner') {
                await onUpdateCoOwners(coOwners.filter(e => e !== email));
            } else {
                await onUpdateCoManagers(coManagers.filter(e => e !== email));
            }
        }
    };

    const handleChangeRole = async (email, currentRole) => {
        const newRole = currentRole === 'co_owner' ? 'co_manager' : 'co_owner';
        const newRoleLabel = newRole === 'co_owner' ? 'Co-propriétaire' : 'Co-organisateur';
        const result = await Swal.fire({
            title: 'Changer le rôle ?',
            text: `Passer ${email} en "${newRoleLabel}" ?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Confirmer',
            cancelButtonText: 'Annuler'
        });
        if (result.isConfirmed) {
            if (currentRole === 'co_owner') {
                await Promise.all([
                    onUpdateCoOwners(coOwners.filter(e => e !== email)),
                    onUpdateCoManagers([...coManagers, email])
                ]);
            } else {
                await Promise.all([
                    onUpdateCoManagers(coManagers.filter(e => e !== email)),
                    onUpdateCoOwners([...coOwners, email])
                ]);
            }
        }
    };

    const inputStyle = {
        width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0',
        fontSize: '14px', marginTop: '5px'
    };

    const labelStyle = {
        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        fontWeight: 'bold', color: '#4a5568', marginTop: '15px'
    };

    return (
        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '700px' }}>
            <h2 style={{ marginTop: 0, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Trophy size={24} color="#3182ce" /> Configuration de l'évènement
            </h2>

            <label style={labelStyle}>Nom officiel</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} disabled={readOnly} style={{...inputStyle, backgroundColor: readOnly ? '#f7fafc' : 'white'}} />

            <label style={labelStyle}><MapPin size={16}/> Adresse du lieu</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} disabled={readOnly} style={{...inputStyle, backgroundColor: readOnly ? '#f7fafc' : 'white'}} />

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                    <label style={labelStyle}><Calendar size={16}/> Date de l'évènement</label>
                    <input type="date" name="event_date" value={formData.event_date} onChange={handleChange} disabled={readOnly} style={{...inputStyle, backgroundColor: readOnly ? '#f7fafc' : 'white'}} />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={labelStyle}><Clock size={16}/> Heure de début</label>
                    <input type="time" name="start_time" value={formData.start_time} onChange={handleChange} disabled={readOnly} style={{...inputStyle, backgroundColor: readOnly ? '#f7fafc' : 'white'}} />
                </div>
            </div>

            <div style={{ marginTop: '25px', padding: '15px', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Logistique Pesée</h3>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Date de pesée</label>
                        <input type="date" name="weigh_in_date" value={formData.weigh_in_date} onChange={handleChange} disabled={readOnly} style={{...inputStyle, backgroundColor: readOnly ? '#f7fafc' : 'white'}} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Heure de pesée</label>
                        <input type="time" name="weigh_in_time" value={formData.weigh_in_time} onChange={handleChange} disabled={readOnly} style={{...inputStyle, backgroundColor: readOnly ? '#f7fafc' : 'white'}} />
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '25px', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: formData.visibility === 'public' ? '#f0fff4' : '#fff5f5' }}>
                <label style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    {formData.visibility === 'public' ? <Globe size={18} color="#38a169"/> : <Lock size={18} color="#e53e3e"/>} 
                    Visibilité de la compétition
                </label>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => !readOnly && setFormData({...formData, visibility: 'public'})}
                        disabled={readOnly}
                        style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', cursor: readOnly ? 'not-allowed' : 'pointer', backgroundColor: formData.visibility === 'public' ? '#38a169' : 'white', color: formData.visibility === 'public' ? 'white' : '#4a5568', fontWeight: 'bold', opacity: readOnly ? 0.6 : 1 }}
                    >
                        Public
                    </button>
                    <button
                        onClick={() => !readOnly && setFormData({...formData, visibility: 'private'})}
                        disabled={readOnly}
                        style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', cursor: readOnly ? 'not-allowed' : 'pointer', backgroundColor: formData.visibility === 'private' ? '#e53e3e' : 'white', color: formData.visibility === 'private' ? 'white' : '#4a5568', fontWeight: 'bold', opacity: readOnly ? 0.6 : 1 }}
                    >
                        Privé
                    </button>
                </div>
                
                {formData.visibility === 'public' && (
                    <div style={{ marginTop: '15px', fontSize: '12px', color: '#2f855a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Share2 size={14} /> Les spectateurs peuvent suivre les résultats en direct via le lien public.
                    </div>
                )}
            </div>
            <button 
                onClick={() => !readOnly && onUpdate(formData)}
                disabled={readOnly}
                style={{ marginTop: '25px', width: '100%', backgroundColor: readOnly ? '#a0aec0' : '#2c5282', color: 'white', padding: '12px', borderRadius: '6px', border: 'none', cursor: readOnly ? 'not-allowed' : 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
                <Save size={18} /> {readOnly ? 'Compétition en cours - Modifications verrouillées' : 'Enregistrer'}
            </button>

            {/* SECTION RÔLES */}
            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '15px', color: '#2d3748', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={17} color="#3182ce" /> Rôles et accès
                </h3>
                <p style={{ fontSize: '12px', color: '#718096', marginBottom: '15px' }}>
                    Récapitulatif des personnes ayant accès à cette compétition.
                </p>

                {/* Propriétaire — avec son email */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', backgroundColor: '#fffbeb', borderRadius: '6px', border: '1px solid #fef3c7', marginBottom: '8px' }}>
                    <Crown size={14} color="#d97706" flexShrink={0} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#92400e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isOwner ? `${currentUserEmail} (vous)` : (ownerEmail || 'Propriétaire')}
                        </div>
                        <div style={{ fontSize: '11px', color: '#a16207' }}>Propriétaire · accès complet et suppression</div>
                    </div>
                </div>

                {/* Co-propriétaires et co-organisateurs */}
                {allMembers.map(({ email, role }) => {
                    const isCoOwnerRow = role === 'co_owner';
                    const isMe = email === currentUserEmail;
                    return (
                        <div key={email} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', backgroundColor: isCoOwnerRow ? '#faf5ff' : '#ebf8ff', borderRadius: '6px', border: `1px solid ${isCoOwnerRow ? '#d6bcfa' : '#bee3f8'}`, marginBottom: '8px' }}>
                            {isCoOwnerRow ? <ShieldCheck size={14} color="#7c3aed" style={{ flexShrink: 0 }} /> : <Users size={14} color="#3182ce" style={{ flexShrink: 0 }} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: isCoOwnerRow ? '#5b21b6' : '#2c5282', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {email}{isMe ? ' (vous)' : ''}
                                </div>
                                <div style={{ fontSize: '11px', color: isCoOwnerRow ? '#7c3aed' : '#2b6cb0' }}>
                                    {isCoOwnerRow ? 'Co-propriétaire · accès complet sauf suppression' : 'Co-organisateur · saisie des résultats uniquement'}
                                </div>
                            </div>
                            {isOwner && (
                                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                    <button
                                        onClick={() => handleChangeRole(email, role)}
                                        title={isCoOwnerRow ? 'Rétrograder en co-organisateur' : 'Promouvoir en co-propriétaire'}
                                        style={{ background: 'none', border: `1px solid ${isCoOwnerRow ? '#c4b5fd' : '#bee3f8'}`, borderRadius: '4px', cursor: 'pointer', color: isCoOwnerRow ? '#7c3aed' : '#3182ce', padding: '3px 6px', fontSize: '10px', fontWeight: 'bold' }}
                                    >
                                        {isCoOwnerRow ? '↓ Org.' : '↑ Prop.'}
                                    </button>
                                    <button onClick={() => handleRemoveMember(email, role)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e', display: 'flex', alignItems: 'center', padding: '2px' }}>
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}

                {allMembers.length === 0 && !isOwner && (
                    <p style={{ fontSize: '12px', color: '#a0aec0', fontStyle: 'italic', textAlign: 'center', padding: '12px' }}>
                        Aucun autre accès configuré.
                    </p>
                )}

                {/* Ajout d'un membre (propriétaire uniquement) */}
                {isOwner && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: allMembers.length > 0 ? '8px' : '0' }}>
                        <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
                            <input
                                type="email"
                                placeholder="Email de la personne à ajouter..."
                                value={newMemberEmail}
                                onChange={e => setNewMemberEmail(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddMember()}
                                style={{ flex: 1, padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '14px' }}
                            />
                            <button
                                onClick={handleAddMember}
                                style={{ padding: '9px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                            >
                                <UserPlus size={16} /> Ajouter
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 14px', borderRadius: '6px', border: `2px solid ${newMemberRole === 'co_manager' ? '#3182ce' : '#e2e8f0'}`, backgroundColor: newMemberRole === 'co_manager' ? '#ebf8ff' : 'white', flex: 1, fontSize: '13px', fontWeight: '600', color: newMemberRole === 'co_manager' ? '#2c5282' : '#718096' }}>
                                <input type="radio" name="newMemberRole" value="co_manager" checked={newMemberRole === 'co_manager'} onChange={() => setNewMemberRole('co_manager')} style={{ accentColor: '#3182ce' }} />
                                <Users size={14} /> Co-organisateur
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 14px', borderRadius: '6px', border: `2px solid ${newMemberRole === 'co_owner' ? '#7c3aed' : '#e2e8f0'}`, backgroundColor: newMemberRole === 'co_owner' ? '#faf5ff' : 'white', flex: 1, fontSize: '13px', fontWeight: '600', color: newMemberRole === 'co_owner' ? '#5b21b6' : '#718096' }}>
                                <input type="radio" name="newMemberRole" value="co_owner" checked={newMemberRole === 'co_owner'} onChange={() => setNewMemberRole('co_owner')} style={{ accentColor: '#7c3aed' }} />
                                <ShieldCheck size={14} /> Co-propriétaire
                            </label>
                        </div>
                        <div style={{ fontSize: '11px', color: '#718096', padding: '6px 10px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            {newMemberRole === 'co_owner'
                                ? '🛡️ Accès complet à la compétition (gestion, athlètes, matchs, résultats) — sauf suppression.'
                                : '📋 Saisie des résultats uniquement depuis l\'onglet Direct.'
                            }
                        </div>
                    </div>
                )}
            </div>

            {!readOnly && isOwner && (
                <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #fed7d7' }}>
                    <h3 style={{ color: '#e53e3e', fontSize: '14px', marginBottom: '10px' }}>Zone de danger</h3>
                    <p style={{ fontSize: '12px', color: '#718096', marginBottom: '15px' }}>
                        La suppression est définitive et effacera toutes les données liées (athlètes, matchs, catégories).
                    </p>
                    <button 
                        onClick={async () => {
                            const captchaCode = Math.random().toString(36).substring(2, 8).toUpperCase();
                            const { value: userInput } = await Swal.fire({
                                title: 'Supprimer définitivement ?',
                                html: `
                                    <div style="text-align: center;">
                                        <p style="color: #e53e3e; font-weight: bold;">CETTE ACTION EST IRRÉVERSIBLE.</p>
                                        <p style="margin: 15px 0;">Veuillez recopier le code suivant pour confirmer :</p>
                                        <div style="background: #edf2f7; padding: 10px; font-family: monospace; font-size: 24px; letter-spacing: 5px; font-weight: bold; border-radius: 8px; margin-bottom: 20px;">
                                            ${captchaCode}
                                        </div>
                                    </div>
                                `,
                                input: 'text',
                                inputPlaceholder: 'Entrez le code ici...',
                                showCancelButton: true,
                                confirmButtonText: 'SUPPRIMER TOUT',
                                confirmButtonColor: '#e53e3e',
                                cancelButtonText: 'Annuler',
                                inputValidator: (value) => {
                                    if (!value) return 'Vous devez saisir le code !';
                                    if (value.toUpperCase() !== captchaCode) return 'Le code est incorrect.';
                                }
                            });

                                if (userInput) {
                                try {
                                    Swal.showLoading();
                                    await deleteCompetitionApi(competition.id);
                                    Swal.fire('Supprimé', 'La compétition a été effacée.', 'success');
                                    if (onDelete) onDelete();
                                } catch (err) {
                                    Swal.fire('Erreur', translateError(err), 'error');
                                }
                            }
                        }}
                        style={{ 
                            width: '100%', backgroundColor: 'white', color: '#e53e3e', 
                            padding: '10px', borderRadius: '6px', border: '1px solid #e53e3e', 
                            cursor: 'pointer', fontWeight: 'bold', display: 'flex', 
                            alignItems: 'center', justifyContent: 'center', gap: '8px' 
                        }}
                    >
                        <Trash2 size={18} /> Supprimer la compétition
                    </button>
                </div>
            )}
        </div>
    );
}
